import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { updateProfile } from 'panel/user/redux/actions'
import { ApplicationState } from 'AppReducer'
import { maskDate, maskPhone } from 'util/masks'

import RemovePicture from './RemovePicture'
import Upload, { UploadRef } from 'util/modules/Upload'

const initialState = {
  code: '',
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  birthday: '',
  maritalStatus: '',
  personType: 'STANDARD',
  docType: '',
  docNumber: '',
  gender: 'O',
  phone: '',
  password: '',
  _password: ''
}

const ProfileForm: React.FC = () => {
  const [picture, setPicture] = useState<File>()
  const [form, setForm] = useState(initialState)

  const uploadRef = useRef<UploadRef>(null)
  const history = useHistory()
  const dispatch = useDispatch()

  const { logged: user } = useSelector((state: ApplicationState) => state.userReducer)

  function handleSubmit (e: React.FormEvent): void {
    e.preventDefault()

    const formData = new FormData()
    if (picture) formData.set('picture', picture)

    Object.entries(form).forEach(([key, value]) => {
      if (value) {
        if (key !== 'birthday') {
          formData.set(key, value as string)
        } else {
          formData.set(key, String(value).split('/').reverse().join('-'))
        }
      }
    })

    updateProfile(formData)(dispatch)
  }

  function handleClear (): void {
    if (uploadRef.current) {
      uploadRef.current.clear()
    }
    setForm(initialState)
    history.push('/panel/user')
  }

  useEffect(() => {
    const person = user?.person
    if (user && person) {
      setForm({
        ...form,
        code: person.code || '',
        firstName: person.firstName || '',
        lastName: person.lastName || '',
        email: person.email || '',
        username: user.username || '',
        birthday: person.birthday ? person.birthday.split('-').reverse().join('/') : '',
        personType: person.personType || 'STANDARD',
        docType: person.docType || '',
        docNumber: person.docNumber || '',
        gender: person.gender || 'O',
        maritalStatus: person.maritalStatus || '',
        phone: person.phone || ''
      })

      if (person.picture) {
        uploadRef.current?.setPreviewImage(person.picture)
      } else {
        uploadRef.current?.clear()
      }
    }
  }, [user])

  const isStandard = form.personType === 'STANDARD'

  return (
    <form onSubmit={handleSubmit} className="row align-start">
      <div className="row panel margin-top-32 mobile-responsive">
        <h2 className="col-12 margin-bottom-16">Dados Básicos</h2>
        <div className="row" style={{ width: 179, height: 190 }}>
          <div className="row" style={{ width: 179, height: 179 }}>
            <Upload
              ref={uploadRef}
              onChange={setPicture}
              placeholder="Selecionar imagem para perfil"
            />
          </div>
          {!!user?.person?.picture && <RemovePicture type="user" />}
        </div>
        <div className="row grow padding-left-16" style={{ marginTop: -16 }}>
          <div className="row">
            <div className="form-control col-6">
              <label>{isStandard ? 'Nome' : 'Razão Social'}</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e): void => setForm({ ...form, firstName: e.target.value })}
                required
              />
            </div>
            <div className="form-control col-6">
              <label>{isStandard ? 'Sobrenome' : 'Nome Fantasia'}</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e): void => setForm({ ...form, lastName: e.target.value })}
                required
              />
            </div>
            <div className="form-control col-6">
              <label>E-mail</label>
              <input
                type="email"
                value={form.email}
                disabled
                required
              />
            </div>
            <div className="form-control col-4">
              <label>Nome de Usuário</label>
              <input
                type="text"
                value={form.username}
                disabled
              />
            </div>
            <div className="form-control col-2">
              <label>Código</label>
              <input
                type="text"
                value={form.code || '...'}
                disabled
              />
            </div>
          </div>
        </div>

        <h2 className="col-12 margin-bottom-16 margin-top-32">Dados Complementares</h2>
        <div className="form-control col-2">
          <label className="label-responsive">Tipo de Cadastro</label>
          <select
            value={form.personType}
            onChange={(e): void => setForm({ ...form, personType: e.target.value as any })}
          >
            <option></option>
            <option value="STANDARD">Pessoa Física</option>
            <option value="BUSINESS">Pessoa Jurídica</option>
          </select>
        </div>
        <div className="form-control col-2">
          <label className="label-responsive document-label">Documento Principal</label>
          <select
            value={form.docType}
            onChange={(e): void => setForm({ ...form, docType: e.target.value as any })}
          >
            <option value="PP">Passaporte</option>
            <option value="DL">CNH</option>
            <option value="ID">RG</option>
            <option value="TIN">CPF</option>
            <option value="BIN">CNPJ</option>
            <option value="EIN">I.E</option>
          </select>
        </div>
        <div className="form-control col-4">
          <label className="label-responsive">Número do Documento Principal</label>
          <input
            type="text"
            value={form.docNumber}
            onChange={({ target: { value } }): void => setForm({ ...form, docNumber: value })}
          />
        </div>
        <div className="form-control col-4">
          <label>Telefone</label>
          <input
            type="text"
            value={form.phone}
            onChange={(e): void => setForm({ ...form, phone: maskPhone(e.target.value) })}
          />
        </div>
        <div className="form-control col-4">
          <label className="label-responsive">Data de Nascimento</label>
          <input
            type="text"
            value={form.birthday}
            onChange={(e): void => setForm({ ...form, birthday: maskDate(e.target.value, '/') })}
          />
        </div>
        <div className="form-control col-4">
          <label className="label-responsive">Gênero</label>
          <select
            value={form.gender}
            onChange={(e): void => setForm({ ...form, gender: e.target.value as any })}
          >
            <option></option>
            <option value="M">Masculino</option>
            <option value="F">Feminino</option>
            <option value="O">Outro</option>
          </select>
        </div>
        <div className="form-control col-4">
          <label className="label-responsive">Estado Civil</label>
          <select
            value={form.maritalStatus || ''}
            onChange={(e): void => setForm({ ...form, maritalStatus: e.target.value as any })}
          >
            <option></option>
            <option value="single">Solteiro(a)</option>
            <option value="married">Casado(a)</option>
            <option value="divorced">Divorciado(a)</option>
            <option value="widower">Viúvo(a)</option>
            <option value="commonLawMarriage">União Estável</option>
          </select>
        </div>

        <div className="row margin-top-16 justify-end responsive-btn">
          <span onClick={handleClear} className="button secondary margin-right-8" style={{ width: 300 }}>
            Limpar
          </span>
          <button className="primary" style={{ width: 300 }}>
            Atualizar Perfil
          </button>
        </div>
      </div>

    </form>
  )
}

export default ProfileForm
